/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import Stack from "../components/ui/Stack";
import Paginator from "../components/ui/Paginator";
import Dimmed from "../components/ui/Dimmed";
import PluginCardSkeleton from "../components/skeleton/PluginCardSkeleton";
import EmptyView from "../components/EmptyView";
import Icon from "../components/ui/Icon";
import MembershipItem from "../components/membership/MembershipItem";
import SearchSortBar from "../components/booking/SearchSortBar";
import { useGetMemberships } from "../lib/react-query/useGetMemberships";
import { useGetPurchasedMemberships } from "../lib/react-query/useGetPurchasedMemberships";
import { useServices } from "../lib/react-query/useServices";
import { useProductsSearchSort } from "../hooks/useProductsSearchSort";
import { useAuth } from "../hooks/useAuth";
import { FlowType, setFlow } from "../store/slices/flowSlice";

const Grid = styled(Stack)`
  grid-template-columns: repeat(auto-fill, minmax(min(314px, 100%), 1fr));
`;

const SERVICE_FILTER_TYPE = "service";

const Memberships = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const { searchValue, debouncedSearch, sortBy, orderField, orderDirection, setSearchValue, setSortBy } =
    useProductsSearchSort();

  const { isAuthenticated } = useAuth();
  const { data: services } = useServices();
  const serviceIds = selectedFilters.filter((f) => f.type === SERVICE_FILTER_TYPE).map((f) => f.id);
  const { data: purchasedData } = useGetPurchasedMemberships({ on_page: 200 }, { enabled: isAuthenticated });
  const purchasedMembershipIds = useMemo(() => {
    const ids = new Set();
    (purchasedData?.data || []).forEach((p) => {
      const id = p.membership?.id ?? p.membership_id;
      if (id != null) ids.add(String(id));
    });
    return ids;
  }, [purchasedData]);

  const { data, isLoading, isFetching, isPlaceholderData } = useGetMemberships(
    { page: currentPage, search: debouncedSearch, orderField, orderDirection: orderDirection?.toLowerCase(), serviceIds },
    { placeholderData: keepPreviousData }
  );

  const serviceFilterItems = (Array.isArray(services) ? services : [])
    .filter((s) => s.memberships?.length)
    .map((s) => ({ id: +s.id, name: s.name }));
  const filterGroups = [{ type: SERVICE_FILTER_TYPE, label: "Services", items: serviceFilterItems }];

  const toggleFilter = (type, id) =>
    setSelectedFilters((prev) =>
      prev.some((f) => f.type === type && f.id === id)
        ? prev.filter((f) => !(f.type === type && f.id === id))
        : [...prev, { type, id }]
    );
  const clearFilters = () => setSelectedFilters([]);

  const memberships = data?.data || [];
  const totalPages = data?.metadata?.pages_count || 1;
  const reserveDescription = memberships.some((m) => m.description);
  const isFiltering = !!debouncedSearch || !!sortBy || serviceIds.length > 0;

  // Reset to the first page whenever the search, sort or service filter changes
  const serviceIdsKey = serviceIds.join(",");
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortBy, serviceIdsKey]);

  useEffect(() => {
    dispatch(setFlow(FlowType.CART));
  }, [dispatch]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <Container>
      <NavigationHeader title="Memberships" navigation cart clientLogin />
      <Stack $gap="1rem">
        {!isLoading && (memberships.length > 0 || isFiltering) && (
          <SearchSortBar
            searchValue={searchValue}
            sortBy={sortBy}
            onSearchChange={setSearchValue}
            onSortChange={setSortBy}
            filterGroups={filterGroups}
            selectedFilters={selectedFilters}
            onFilterChange={toggleFilter}
            onClearFilters={clearFilters}
            filterNote="Filter memberships by their included services"
          />
        )}

        {isLoading && (
          <Grid $gap="0.5rem">
            <PluginCardSkeleton withIncludes />
          </Grid>
        )}

        {!isLoading && memberships.length === 0 && (
          <EmptyView icon={Icon.Membership} description="No memberships available" />
        )}

        {!isLoading && memberships.length > 0 && (
          <>
            <Dimmed $active={isPlaceholderData}>
              <Grid $gap="0.5rem">
                {memberships.map((membership) => (
                  <MembershipItem
                    key={membership.id}
                    membership={membership}
                    reserveDescription={reserveDescription}
                    isPurchased={purchasedMembershipIds.has(String(membership.id))}
                  />
                ))}
              </Grid>
            </Dimmed>
            {totalPages > 1 && (
              <Paginator
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isFetching}
              />
            )}
          </>
        )}
      </Stack>
    </Container>
  );
};

export default Memberships;
